import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Breadcrumbs, Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import TranslateIcon from "@mui/icons-material/Translate";
import { getDsokSystemSettings } from "app/services/api/dsokSystemSettingsApi";

const SETTING_BOXES = [
  {
    id: "colors",
    icon: PaletteIcon,
    titleKey: "pages.systemSettings.newCustomerSettingsTabs.colors",
    descriptionKey: "pages.systemSettings.newCustomerSettingsColorsDescription",
    path: "/dsok/system-settings/new-customer-settings/colors",
  },
  {
    id: "translations",
    icon: TranslateIcon,
    titleKey: "pages.systemSettings.newCustomerSettingsTabs.translations",
    descriptionKey: "pages.systemSettings.newCustomerSettingsTranslationsDescription",
    path: "/dsok/system-settings/new-customer-settings/translations",
  },
];

function SettingBox({ icon: Icon, titleKey, descriptionKey, path }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: "box-shadow 0.2s, background-color 0.2s",
        "&:hover": {
          boxShadow: 1,
          bgcolor: "action.hover",
        },
      }}
      onClick={() => navigate(path)}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "action.hover",
              flexShrink: 0,
            }}
          >
            {Icon && <Icon sx={{ fontSize: 28, color: "primary.main" }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t(titleKey, { defaultValue: titleKey.split(".").pop() })}
            </Typography>
            {descriptionKey && (
              <Typography variant="body2" color="text.secondary">
                {t(descriptionKey)}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function NewCustomerSettingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getDsokSystemSettings()
      .then(() => { if (!cancelled) setLoading(false); })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            {t("sidebar.menuItem.home")}
          </Link>
          <Link
            to="/dsok/system-settings"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("pages.systemSettings.breadcrumb", { defaultValue: "Nastavenia" })}
          </Link>
          <Typography color="text.primary">
            {t("pages.systemSettings.newCustomerSettingsBreadcrumb", {
              defaultValue: "Nastavenie nového zákazníka",
            })}
          </Typography>
        </Breadcrumbs>
      </Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t("pages.systemSettings.customerSettingsTitle", {
          defaultValue: "Nastavenie nového zákazníka",
        })}
      </Typography>

      <Grid container spacing={2}>
        {SETTING_BOXES.map((box) => (
          <Grid item xs={12} sm={6} md={3} key={box.id}>
            <SettingBox
              icon={box.icon}
              titleKey={box.titleKey}
              descriptionKey={box.descriptionKey}
              path={box.path}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
